import { useState } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import '../styles/pricing.css';

const CHECK = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="20 6 9 17 4 12"/>
  </svg>
);

const PLANS = [
  {
    name: 'Basic',
    badge: null,
    amountCAD: '$29',
    usdEquiv: '~$21 USD',
    period: '/month',
    note: 'No setup fee · Cancel anytime',
    desc: 'Perfect for small businesses that want to stop missing website visitors. Your AI chat agent answers questions, captures leads, and responds instantly — 24/7, even when you\'re closed.',
    features: [
      'AI website chat agent, branded as your business',
      '24/7 automated website chat',
      'Lead capture and contact collection',
      'Automated email follow-ups',
      'Knowledge base trained on your website',
      'Cancel anytime',
    ],
    cta: 'Get Started',
    // TODO: replace with the new $29 CAD Stripe payment link
    ctaUrl: 'https://buy.stripe.com/4gM5kEfllcfn78i5Mx5wI04',
    ctaInternal: false,
    featured: false,
  },
  {
    name: 'Growth',
    badge: 'Recommended',
    amountCAD: '$99',
    usdEquiv: '~$72 USD',
    period: '/month',
    note: 'No setup fee · Cancel anytime',
    desc: 'For businesses ready to capture every lead — on your website and on the phone. Your AI chat and voice agents work together to qualify leads, book appointments, and answer calls 24/7 without hiring extra staff.',
    features: [
      'Everything in Basic',
      'AI chat + AI phone agent',
      'Answers your calls 24/7',
      'Appointment booking via chat & phone',
      '100 voice minutes included',
      'Lead qualification and routing',
      'API & webhook integrations',
      'Priority support',
    ],
    cta: 'Get Started',
    // TODO: replace with the new $99 CAD Stripe payment link
    ctaUrl: 'https://buy.stripe.com/5kQdRagppfrz50a1wh5wI03',
    ctaInternal: false,
    featured: true,
  },
  {
    name: 'Advanced',
    badge: null,
    amountCAD: null,
    usdEquiv: null,
    period: '/month',
    note: 'Custom scope · No setup fee · No long-term contract',
    desc: 'The full AI stack for growing businesses. Multiple chat and voice agents, deep integrations, and a knowledge base trained on your business — so your AI sounds like you, handles more volume, and connects to the tools you already use.',
    features: [
      'Everything in Growth',
      'Multiple AI chat agents',
      'Multiple AI voice phone numbers',
      'Custom integrations & webhooks',
      'Custom AI training (website + documents)',
      'Higher usage limits',
      'Multi-language support',
      'Conversation history & analytics',
      'Dedicated onboarding',
    ],
    cta: 'Contact Us',
    ctaUrl: '/contact',
    ctaInternal: true,
    featured: false,
  },
];

const FAQS = [
  {
    q: 'Is there a contract?',
    a: 'No. Cancel anytime — no lock-in, no cancellation fees.',
  },
  {
    q: 'Is there a setup fee?',
    a: 'No. Founding customers pay no setup fee and no long-term contract, and your monthly price is locked for 12 months.',
  },
  {
    q: 'What happens after I pay?',
    a: 'You\'ll be redirected to Stripe for secure payment. After successful payment, you\'re automatically logged into your Klaivox dashboard and can start setting up your AI agent immediately.',
  },
  {
    q: 'How long does setup take?',
    a: 'Most businesses are live within 24 hours of signing up. Our white-glove onboarding team will configure everything for you.',
  },
  {
    q: 'Can it work with my current tools?',
    a: 'Yes. Klaivox supports Google Calendar, Stripe, WhatsApp, Facebook, Instagram, and connects to your existing tools via API & webhooks (Growth plan and above).',
  },
  {
    q: 'What\'s the difference between chat and voice agents?',
    a: 'Chat agents handle your website visitors — answering questions and capturing leads 24/7. Voice agents pick up your phone calls, qualify callers, and book appointments automatically. The Growth plan includes both.',
  },
];

function FoundingOfferBanner() {
  return (
    <div className="pricing-promo-banner" role="note" aria-label="Founding customer offer">
      <div className="pricing-promo-banner__glow" aria-hidden="true" />
      <div className="pricing-promo-banner__inner">
        <span className="pricing-promo-banner__icon" aria-hidden="true">
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 12 20 22 4 22 4 12"/><rect x="2" y="7" width="20" height="5"/><line x1="12" y1="22" x2="12" y2="7"/>
            <path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/><path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>
          </svg>
        </span>
        <span className="pricing-promo-banner__text">
          <strong>Founding Customer Offer</strong> — No setup fee · No long-term contract · Price locked for 12 months.
        </span>
        <span className="pricing-promo-banner__pill">First 10 customers only</span>
      </div>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'open' : ''}`}>
      <button className="faq-item__question" onClick={() => setOpen(o => !o)} aria-expanded={open}>
        {q}
        <span className="faq-item__icon">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
          </svg>
        </span>
      </button>
      <div className="faq-item__answer" aria-hidden={!open}><p>{a}</p></div>
    </div>
  );
}

export default function Pricing() {
  return (
    <main>

      {/* ===== HERO ===== */}
      <section className="pricing-hero" aria-label="Pricing hero">
        <div className="pricing-hero__blob pricing-hero__blob--1" aria-hidden="true" />
        <div className="pricing-hero__blob pricing-hero__blob--2" aria-hidden="true" />
        <div className="pricing-hero__content">
          <span className="section-label">Pricing</span>
          <h1>Hire an AI Employee for Less Than One Shift a Month</h1>
          <p>Never miss a lead again. Your AI answers calls, books appointments, and follows up with customers 24/7 — without adding payroll. Pay securely via Stripe and get instant dashboard access.</p>
        </div>
      </section>

      {/* ===== FOUNDING OFFER BANNER ===== */}
      <FoundingOfferBanner />

      {/* ===== PRICING CARDS ===== */}
      <section className="pricing-section" aria-label="Pricing plans">
        <div className="pricing-section__inner">
          <AnimatedSection>
            <div className="pricing-cards">
              {PLANS.map((plan, i) => (
                <div
                  className={`pricing-card animate-child ${plan.featured ? 'pricing-card--featured' : ''}`}
                  key={i}
                >
                  {plan.badge && (
                    <div className="pricing-card__badge">{plan.badge}</div>
                  )}
                  <div className="pricing-card__name">{plan.name}</div>

                  {plan.amountCAD ? (
                    <div className="pricing-card__price">
                      <span className="pricing-card__amount">{plan.amountCAD}</span>
                      <div className="pricing-card__price-meta">
                        <span className="pricing-card__currency-tag">CAD</span>
                        <span className="pricing-card__period">{plan.period}</span>
                      </div>
                    </div>
                  ) : (
                    <div className="pricing-card__price">
                      <span className="pricing-card__amount pricing-card__amount--custom">Custom</span>
                    </div>
                  )}
                  {plan.usdEquiv && (
                    <div className="pricing-card__usd">{plan.usdEquiv}</div>
                  )}
                  {plan.note && (
                    <div className="pricing-card__onboarding">{plan.note}</div>
                  )}

                  <p className="pricing-card__desc">{plan.desc}</p>
                  <hr className="pricing-card__divider" />
                  <ul className="pricing-card__features">
                    {plan.features.map((f, j) => (
                      <li key={j}>{CHECK}{f}</li>
                    ))}
                  </ul>

                  {plan.ctaInternal ? (
                    <Link
                      to={plan.ctaUrl}
                      className={`pricing-card__cta ${plan.featured ? 'pricing-card__cta--primary' : 'pricing-card__cta--outline'}`}
                    >
                      {plan.cta} →
                    </Link>
                  ) : (
                    <a
                      href={plan.ctaUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={`pricing-card__cta ${plan.featured ? 'pricing-card__cta--primary' : 'pricing-card__cta--outline'}`}
                    >
                      {plan.cta} →
                    </a>
                  )}
                </div>
              ))}
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <p className="pricing-stripe-note">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
              </svg>
              Secure checkout powered by Stripe. Instant dashboard access after payment.
            </p>
            <p className="pricing-currency-note">All prices are in Canadian dollars.</p>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== WHY KLAIVOX ===== */}
      <section className="why-section section" aria-label="Why businesses choose Klaivox">
        <div className="why-section__inner">
          <AnimatedSection>
            <div className="why-section__header">
              <span className="section-label">Why Klaivox</span>
              <h2>Why Businesses Choose Klaivox</h2>
            </div>
          </AnimatedSection>
          <AnimatedSection delay={0.1}>
            <div className="why-grid">
              {['Never miss another lead', 'Reply faster than competitors', 'Reduce admin workload', 'Operate 24/7', 'Save on staffing costs', 'Scale without hiring'].map((item, i) => (
                <div className="why-item animate-child" key={i}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#4F8EF7" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12"/>
                  </svg>
                  {item}
                </div>
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== FAQ ===== */}
      <section className="faq-section" aria-label="Frequently asked questions">
        <div className="faq-section__inner">
          <AnimatedSection>
            <div className="faq-section__header">
              <span className="section-label">FAQ</span>
              <h2>Common Questions</h2>
            </div>
          </AnimatedSection>
          <AnimatedSection>
            <div className="faq-list">
              {FAQS.map((faq, i) => (
                <FaqItem key={i} q={faq.q} a={faq.a} />
              ))}
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="cta-banner" aria-label="Call to action">
        <div className="cta-banner__blob" aria-hidden="true" />
        <AnimatedSection>
          <div className="cta-banner__inner">
            <h2>Your Next Customer Should Never Go Unanswered.</h2>
            <p className="cta-banner__sub">Launch your branded AI assistant today. Instant access after payment.</p>
            <div className="cta-banner__btns">
              <a href={PLANS[1].ctaUrl} target="_blank" rel="noopener noreferrer" className="btn-primary">
                Get Started with Growth →
              </a>
              <a href={PLANS[0].ctaUrl} target="_blank" rel="noopener noreferrer" className="btn-secondary">
                Start with Basic
              </a>
            </div>
          </div>
        </AnimatedSection>
      </section>

    </main>
  );
}
